import { createSelector } from 'reselect';

import { isPreAssessmentMerchant, preAssessmentDetails } from '@/selectors/merchant';
import { getForcedRegistrationIdCountries } from '@/selectors/options';

export const isSubmitted = createSelector(preAssessmentDetails, ({ isSubmitted }) => !!isSubmitted);

export const showOverview = createSelector(
  preAssessmentDetails,
  ({ showOverview }) => !!showOverview,
);

export const hasUnderwritingInfo = createSelector(
  isPreAssessmentMerchant,
  isSubmitted,
  preAssessmentDetails,
  (
    isPreAssessmentMerchant,
    isSubmitted,
    { goodsCategory, yearlyAcquiredVolume, paymentDelay, paymentInterval },
  ) =>
    isPreAssessmentMerchant &&
    !isSubmitted &&
    !!goodsCategory &&
    (!!yearlyAcquiredVolume || yearlyAcquiredVolume === 0) &&
    (!!paymentDelay || paymentDelay === 0) &&
    !!paymentInterval,
);

export const hasAtLeastOneBusinessConcept = createSelector(
  isPreAssessmentMerchant,
  isSubmitted,
  preAssessmentDetails,
  (isPreAssessmentMerchant, isSubmitted, { businessConcepts }) =>
    isPreAssessmentMerchant && !isSubmitted && !!businessConcepts && businessConcepts.length > 0,
);

export const hasCompanyDetails = createSelector(
  isPreAssessmentMerchant,
  isSubmitted,
  preAssessmentDetails,
  getForcedRegistrationIdCountries,
  (
    isPreAssessmentMerchant,
    isSubmitted,
    {
      registrationCountry,
      operatingCountry,
      registrationNumber,
      registrationAuthority,
      companyName,
      storeUrl,
    },
    forcedRegistrationIdCountries,
  ) => {
    const isRegNumberOptional =
      !forcedRegistrationIdCountries.includes(registrationCountry) || !!registrationNumber;

    return (
      isPreAssessmentMerchant &&
      !isSubmitted &&
      !!registrationCountry &&
      !!operatingCountry &&
      // if DE, registration authority is only required when registration number exist
      ((registrationCountry === 'DE' && (registrationAuthority || !registrationNumber)) ||
        (registrationCountry !== 'DE' && !registrationAuthority)) &&
      (isRegNumberOptional || !!registrationNumber) &&
      !!companyName &&
      !!storeUrl
    );
  },
);

export const hasAtLeastOneSellingCountry = createSelector(
  isPreAssessmentMerchant,
  isSubmitted,
  preAssessmentDetails,
  (isPreAssessmentMerchant, isSubmitted, { sellingCountries = { all: [] } }) =>
    isPreAssessmentMerchant && !isSubmitted && sellingCountries.all.length > 0,
);

export const hasOneSellingCountry = createSelector(
  isPreAssessmentMerchant,
  isSubmitted,
  preAssessmentDetails,
  (isPreAssessmentMerchant, isSubmitted, { sellingCountries = { all: [] } }) =>
    isPreAssessmentMerchant && !isSubmitted && sellingCountries.all.length === 1,
);

export const hasMainSellingCountry = createSelector(
  isPreAssessmentMerchant,
  isSubmitted,
  hasAtLeastOneSellingCountry,
  preAssessmentDetails,
  (
    isPreAssessmentMerchant,
    isSubmitted,
    hasAtLeastOneSellingCountry,
    { sellingCountries = null },
  ) =>
    isPreAssessmentMerchant &&
    !isSubmitted &&
    hasAtLeastOneSellingCountry &&
    !!sellingCountries.main &&
    sellingCountries.all.includes(sellingCountries.main),
);

export const hasAtLeastOneProduct = createSelector(
  isPreAssessmentMerchant,
  isSubmitted,
  preAssessmentDetails,
  (isPreAssessmentMerchant, isSubmitted, { products }) =>
    isPreAssessmentMerchant && !isSubmitted && !!products && products.length > 0,
);

export const hasCategory = createSelector(preAssessmentDetails, ({ goodsCategory }) => category =>
  goodsCategory === category,
);

/**
 * returns a function after selector is called with state, which takes business
 * concept ID as an input and returns true if merchant has selected that business concept
 */
export const hasBusinessConcept = createSelector(
  preAssessmentDetails,
  ({ businessConcepts }) => businessConcept =>
    businessConcepts && businessConcepts.includes(businessConcept),
);

const FCA_PRODUCTS = ['FINANCING_FLEXIBLE', 'SLICE_IT_CREDIT', 'ONE_KLARNA_WITH_FINANCING'];
const FCA_COUNTRY = 'GB';
export const shouldCollectFCANumber = createSelector(preAssessmentDetails, ({ products = [] }) =>
  products.some(
    ({ products: countryProducts, country }) =>
      countryProducts.some(({ product }) => FCA_PRODUCTS.includes(product)) &&
      country === FCA_COUNTRY,
  ),
);

export const needsRefreshOnQuestionnaires = createSelector(
  preAssessmentDetails,
  ({ needsRefreshOnQuestionnaires }) => needsRefreshOnQuestionnaires,
);

export const canShowSummary = createSelector(
  hasAtLeastOneProduct,
  hasAtLeastOneSellingCountry,
  hasCompanyDetails,
  hasMainSellingCountry,
  hasUnderwritingInfo,
  hasAtLeastOneBusinessConcept,
  needsRefreshOnQuestionnaires,
  isSubmitted,
  showOverview,
  (
    hasAtLeastOneProduct,
    hasAtLeastOneSellingCountry,
    hasCompanyDetails,
    hasMainSellingCountry,
    hasUnderwritingInfo,
    hasAtLeastOneBusinessConcept,
    needsRefreshOnQuestionnaires,
    isSubmitted,
    showOverview,
  ) =>
    (hasAtLeastOneProduct &&
      hasAtLeastOneSellingCountry &&
      hasCompanyDetails &&
      hasMainSellingCountry &&
      hasUnderwritingInfo &&
      hasAtLeastOneBusinessConcept &&
      !needsRefreshOnQuestionnaires) ||
    (isSubmitted && !showOverview),
);
